import { useEffect, useRef, useState } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import BackgroundFX from '../components/backgroundFX'
import CountryCodeSelect from '../components/countryCodeSelect'
import FormField from '../components/formField'
import Logo from '../components/logo'
import ThemeToggle from '../components/themeToggle'
import HelpButton from '../components/helpButton'
import { STRINGS } from '../constants/strings'
import { useAuth } from '../hooks'

function AuroraBackground() { return null }

const TYPE_OPTIONS = [
  { value: 'user',         label: 'Examinee',     desc: 'Take exams and track your progress' },
  { value: 'organisation', label: 'Organisation', desc: 'Create and manage exams for your users' },
]

function TypeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [])

  const selected = TYPE_OPTIONS.find(o => o.value === value)

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm outline-none transition-all text-left"
        style={{
          background:  'var(--surface-2)',
          border:      `1px solid ${open ? 'var(--brand)' : 'var(--border)'}`,
          color:       'var(--text-1)',
          boxShadow:   open ? '0 0 0 2px color-mix(in srgb, var(--brand) 20%, transparent)' : 'none',
        }}
      >
        <span>{selected?.label}</span>
        <svg className={`w-3.5 h-3.5 transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`}
             style={{ color: 'var(--text-3)' }}
             viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M2 4l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 rounded-xl overflow-hidden shadow-2xl z-50"
             style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          {TYPE_OPTIONS.map(opt => {
            const sel = opt.value === value
            return (
              <button
                key={opt.value}
                type="button"
                onMouseDown={e => { e.preventDefault(); onChange(opt.value); setOpen(false) }}
                className="w-full text-left px-4 py-3 transition-colors last:border-0"
                style={{
                  background:   sel ? 'color-mix(in srgb, var(--brand) 10%, var(--surface))' : 'transparent',
                  borderBottom: '1px solid var(--border)',
                }}
                onMouseOver={e => { if (!sel) e.currentTarget.style.background = 'var(--surface-2)' }}
                onMouseOut={e  => { if (!sel) e.currentTarget.style.background = 'transparent' }}
              >
                <p className="text-sm font-medium" style={{ color: sel ? 'var(--brand)' : 'var(--text-1)' }}>{opt.label}</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>{opt.desc}</p>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}


export default function RegisterPage() {
  const { register, verifyOTP, resendOTP, isAuthenticated } = useAuth()
  const navigate  = useNavigate()
  const [role,        setRole]        = useState('user')
  const [form,        setForm]        = useState({ name: '', email: '', password: '', confirm: '', inviteCode: '', countryCode: '+91', phone: '' })
  const [agreed,      setAgreed]      = useState(false)
  const [error,       setError]       = useState('')
  const [loading,     setLoading]     = useState(false)
  const [pendingName, setPendingName] = useState(null)  // org pending
  const [userPending, setUserPending] = useState(null)  // user pending org approval
  const [otpState,    setOtpState]    = useState(null) // { userId, email }
  const [otp,         setOtp]         = useState('')
  const [resendMsg,   setResendMsg]   = useState('')
  const [resending,   setResending]   = useState(false)

  if (isAuthenticated) return <Navigate to="/" replace />

  const set = k => v => setForm(f => ({ ...f, [k]: v }))

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (form.password !== form.confirm) { setError('Passwords do not match'); return }
    setLoading(true)
    try {
      const phone = form.phone.trim() ? { countryCode: form.countryCode, number: form.phone.trim() } : undefined
      const res = await register({ name: form.name, email: form.email, password: form.password, role, inviteCode: form.inviteCode, phone })
      setOtpState({ userId: res.userId, email: form.email, role: res.role })
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleVerifyOTP(e) {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      const res = await verifyOTP(otpState.userId, otp)
      if (res.role === 'organisation') {
        setPendingName(res.name)
        setOtpState(null)
      } else if (res.role === 'user' && res.status === 'pending') {
        setUserPending(res.name)
        setOtpState(null)
      } else {
        navigate('/')
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    setResending(true)
    try {
      await resendOTP(otpState.userId)
      setResendMsg('OTP resent! Check your email.')
      setTimeout(() => setResendMsg(''), 4000)
    } catch (err) {
      setError(err.message)
    } finally {
      setResending(false)
    }
  }

  if (otpState) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative"
         style={{ background: 'var(--bg)' }}>
      <BackgroundFX />
      <ThemeToggle fixed />
      <HelpButton fixed />
      <div className="relative z-10 w-full sm:max-w-[60%]">
        <div className="card p-10 sm:p-12 sm:px-20">
          <div className="text-center mb-6"><Logo size="auth" /></div>
          <h2 className="text-lg font-bold text-center mb-1" style={{ color: 'var(--text-1)' }}>Verify your email</h2>
          <p className="text-sm text-center mb-6" style={{ color: 'var(--text-3)' }}>
            We sent a 6-digit OTP to <span className="font-semibold" style={{ color: 'var(--text-1)' }}>{otpState.email}</span>
          </p>
          <form onSubmit={handleVerifyOTP} className="space-y-4">
            <input
              type="text" inputMode="numeric" maxLength={6}
              value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
              placeholder="Enter 6-digit OTP"
              className="w-full px-4 py-3 rounded-xl text-sm text-center tracking-widest font-bold outline-none transition-all"
              style={{ background: 'var(--surface-2)', color: 'var(--text-1)', boxShadow: '0 0 0 1px var(--border)', letterSpacing: '0.3em' }}
              onFocus={e => e.currentTarget.style.boxShadow = '0 0 0 2px var(--brand)'}
              onBlur={e  => e.currentTarget.style.boxShadow = '0 0 0 1px var(--border)'}
            />
            {error && (
              <p className="text-xs px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30" style={{ color: 'var(--text-1)' }}>{error}</p>
            )}
            {resendMsg && (
              <p className="text-xs text-center" style={{ color: '#34d399' }}>{resendMsg}</p>
            )}
            <button type="submit" disabled={loading || otp.length !== 6} className="btn-primary w-full justify-center disabled:opacity-60">
              {loading ? <><span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />Verifying…</> : 'Verify Email'}
            </button>
          </form>
          <p className="text-xs text-center mt-4 flex items-center justify-center gap-1.5" style={{ color: 'var(--text-3)' }}>
            Didn't receive it?{' '}
            <button onClick={handleResend} disabled={resending} className="font-medium hover:underline flex items-center gap-1 disabled:opacity-60" style={{ color: 'var(--brand)' }}>
              {resending && <span className="w-3 h-3 rounded-full border-2 animate-spin flex-shrink-0" style={{ borderColor: 'color-mix(in srgb, var(--brand) 30%, transparent)', borderTopColor: 'var(--brand)' }} />}
              Resend OTP
            </button>
          </p>
        </div>
      </div>
    </div>
  )

  if (userPending) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative"
         style={{ background: 'var(--bg)' }}>
      <BackgroundFX />
      <ThemeToggle fixed />
      <HelpButton fixed />
      <div className="relative z-10 w-full sm:max-w-[60%] text-center">
        <div className="card p-10 sm:p-12 sm:px-20">
          <div className="text-center mb-6"><Logo size="auth" /></div>
          <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-1)' }}>Pending Approval</h2>
          <p className="text-sm mb-1" style={{ color: 'var(--text-2)' }}>
            Hi <span className="font-semibold">{userPending}</span>, your account has been created.
          </p>
          <p className="text-sm mb-6" style={{ color: 'var(--text-3)' }}>
            Your organisation will review and approve your account. You'll be able to log in once approved.
          </p>
          <button onClick={() => navigate('/login')} className="btn-primary w-full justify-center">
            Back to Login
          </button>
        </div>
      </div>
    </div>
  )

  if (pendingName) return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative"
         style={{ background: 'var(--bg)' }}>
      <BackgroundFX />
      <ThemeToggle fixed />
      <HelpButton fixed />
      <div className="relative z-10 w-full sm:max-w-[60%] text-center">
        <div className="card p-10 sm:p-12 sm:px-20">
          <div className="text-center mb-6"><Logo size="auth" /></div>
          <h2 className="text-lg font-bold mb-2" style={{ color: 'var(--text-1)' }}>Pending Approval</h2>
          <p className="text-sm mb-1" style={{ color: 'var(--text-2)' }}>
            Hi <span className="font-semibold">{pendingName}</span>, your organisation account has been created.
          </p>
          <p className="text-sm mb-6" style={{ color: 'var(--text-3)' }}>
            The RADTAR Team will review and approve your account. You'll be able to log in once approved.
          </p>
          <button onClick={() => navigate('/login')} className="btn-primary w-full justify-center">
            Back to Login
          </button>
        </div>
      </div>
    </div>
  )

  return (

    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative"
         style={{ background: 'var(--bg)' }}>
      <AuroraBackground />
      <BackgroundFX />
      <ThemeToggle fixed />
      <HelpButton fixed />

      <div className="relative z-10 w-full sm:max-w-[60%]">
        <div className="card p-6 sm:p-10 md:p-12 md:px-20">
          <div className="text-center mb-6">
            <Logo size="auth" />
          </div>
          <div className="text-center mb-6">
            <h2 className="text-xl font-bold mb-1" style={{ color: 'var(--text-1)' }}>
              {STRINGS.auth.registerTitle}
            </h2>
            <p className="text-sm" style={{ color: 'var(--text-3)' }}>
              {STRINGS.auth.registerSubtitle}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Type */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium" style={{ color: 'var(--text-2)' }}>Type</label>
              <TypeDropdown value={role} onChange={v => { setRole(v); setForm(f => ({ ...f, inviteCode: '' })) }} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label={role === 'organisation' ? 'Organisation Name' : STRINGS.auth.nameLabel} type="text" value={form.name}
                onChange={set('name')} placeholder={role === 'organisation' ? 'Organisation name' : STRINGS.auth.namePlaceholder} autoComplete="name" maxLength={50} />
              <FormField label={STRINGS.auth.emailLabel} type="email" value={form.email}
                onChange={set('email')} placeholder={STRINGS.auth.emailPlaceholder} autoComplete="email" maxLength={100} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <FormField label={STRINGS.auth.passwordLabel} type="password" value={form.password}
                onChange={set('password')} placeholder={STRINGS.auth.passwordPlaceholder} autoComplete="new-password" maxLength={20} />
              <FormField label={STRINGS.auth.confirmLabel} type="password" value={form.confirm}
                onChange={set('confirm')} placeholder={STRINGS.auth.confirmPlaceholder} autoComplete="new-password" maxLength={20} />
            </div>

            {/* Phone (optional) */}
            <div className="space-y-1.5">
              <label className="block text-xs font-medium" style={{ color: 'var(--text-2)' }}>
                Mobile Number <span style={{ color: 'var(--text-3)' }}>(optional)</span>
              </label>
              <div className="flex gap-2">
                <CountryCodeSelect value={form.countryCode} onChange={v => set('countryCode')(v)} />
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => set('phone')(e.target.value.replace(/\D/g, ''))}
                  placeholder="Mobile number"
                  autoComplete="tel"
                  maxLength={15}
                  className="flex-1 min-w-0 px-4 py-3 rounded-xl text-sm outline-none transition-shadow"
                  style={{ background: 'var(--surface-2)', color: 'var(--text-1)', boxShadow: '0 0 0 1px var(--border)' }}
                  onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px var(--brand)' }}
                  onBlur={e  => { e.currentTarget.style.boxShadow = '0 0 0 1px var(--border)' }}
                />
              </div>
            </div>

            {role === 'user' && (
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <label className="text-xs font-medium" style={{ color: 'var(--text-2)' }}>{STRINGS.auth.inviteCodeLabel}</label>
                  <div className="relative group">
                    <span className="flex items-center justify-center w-3.5 h-3.5 rounded-full text-[9px] font-bold cursor-default select-none"
                          style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text-3)' }}>i</span>
                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-56 px-3 py-2.5 rounded-xl text-xs leading-relaxed shadow-2xl z-50
                                    opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity"
                         style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-2)' }}>
                      <p><span className="font-semibold" style={{ color: 'var(--text-1)' }}>{STRINGS.auth.inviteCodeTooltipWith.label}</span> — {STRINGS.auth.inviteCodeTooltipWith.desc}</p>
                      <p><span className="font-semibold" style={{ color: 'var(--text-1)' }}>{STRINGS.auth.inviteCodeTooltipWithout.label}</span> — {STRINGS.auth.inviteCodeTooltipWithout.desc}</p>
                      <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 rotate-45"
                           style={{ background: 'var(--surface)', borderRight: '1px solid var(--border)', borderBottom: '1px solid var(--border)', marginTop: '-5px' }} />
                    </div>
                  </div>
                </div>
                <input
                  type="text"
                  value={form.inviteCode}
                  onChange={e => set('inviteCode')(e.target.value)}
                  placeholder={STRINGS.auth.inviteCodePlaceholder}
                  autoComplete="off"
                  maxLength={7}
                  className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-shadow"
                  style={{ background: 'var(--surface-2)', color: 'var(--text-1)', boxShadow: '0 0 0 1px var(--border)' }}
                  onFocus={e => { e.currentTarget.style.boxShadow = '0 0 0 2px var(--brand)' }}
                  onBlur={e  => { e.currentTarget.style.boxShadow = '0 0 0 1px var(--border)' }}
                />
                <p className="text-[0.7rem]" style={{ color: 'var(--text-3)' }}>{STRINGS.auth.inviteCodeHint}</p>
              </div>
            )}

            {/* Terms */}
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={agreed}
                onChange={e => setAgreed(e.target.checked)}
                className="mt-0.5 shrink-0 accent-[var(--brand)] w-4 h-4 cursor-pointer"
              />
              <span className="text-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>
                I agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80" style={{ color: 'var(--brand)' }}>Terms & Conditions</a>
                {' '}and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80" style={{ color: 'var(--brand)' }}>Privacy Policy</a>
              </span>
            </label>

            {error && (
              <p className="text-xs px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 animate-fade-up">
                {error}
              </p>
            )}

            <button type="submit" disabled={loading || !agreed || !form.name.trim() || !form.email.trim() || !form.password || !form.confirm}
              className="btn-primary w-full justify-center disabled:opacity-60">
              {loading
                ? <><span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />{STRINGS.auth.registerLoading}</>
                : STRINGS.auth.registerCta}
            </button>
          </form>

          <p className="text-xs text-center mt-5" style={{ color: 'var(--text-3)' }}>
            {STRINGS.auth.registerSwitch}{' '}
            <Link to="/login" className="font-medium hover:underline" style={{ color: 'var(--brand)' }}>
              {STRINGS.auth.registerSwitchLink}
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
